import fetchClient from './fetchClient';
import { offlineDb } from 'utils/offlineDb';

import { errorHandler } from './utils';


const config = (isExternalPage: boolean = false) => {
    return {
        headers: {
            Authorization:
                'Bearer ' +
                localStorage.getItem(
                    isExternalPage ? 'delivery-token' : 'token',
                ),
        },
    };
};

export const getOrderDashboard = async () => {
    const res: any = await fetchClient
        .get('/orders/dashboard', config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const exportOrders = async (range: any) => {
    const fileConfig: any = {
        headers: {
            Authorization: config(false).headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    let url = `/orders/export`;
    if (range) {
        url += `?start=${range.startDate}&end=${range.endDate}`;
    }
    const res = await fetchClient.get(url, fileConfig).catch((err) => {
        errorHandler(err);
    });
    if (!res || !res.data) return null;
    return new Blob([res.data], { type: 'text/csv' });
};

export const exportDeliveryNotes = async (orderIds: string[]) => {
    const fileConfig: any = {
        headers: {
            Authorization: config(false).headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient.post('/orders/export/delivery-notes', { orderIds }, fileConfig).catch((err) => {
        errorHandler(err);
    });
    if (!res || !res.data) return null;
    return new Blob([res.data], { type: 'application/zip' });
};

export const exportPreparationSheets = async (orderIds: string[]) => {
    const fileConfig: any = {
        headers: {
            Authorization: config(false).headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient.post('/orders/export/preparation-sheets', { orderIds }, fileConfig).catch((err) => {
        errorHandler(err);
    });
    if (!res || !res.data) return null;
    return new Blob([res.data], { type: 'application/pdf' });
};

export const getNotInvoicedOrders = async (
    page: number,
    limit: number,
    start: string,
    end: string,
    period: string | null,
    customerId: string | null,
) => {
    let query = `?page=${page}&limit=${limit}&start=${start}&end=${end}`;
    if (customerId) query += `&customerId=${customerId}`;
    if (period) query += `&period=${period}`;

    const res: any = await fetchClient
        .get(`/orders/not-invoiced${query}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getPaginatedOrders = async (
    page: number,
    limit: number,
    status: string,
    search: string,
    isReadyStatus: string,
    sortBy: string = 'deliveryDate',
    range: any = null,
    tourId: string = 'all',
) => {
    let url = `/orders?limit=${limit}&page=${page}&status=${status}&search=${search}&isReadyStatus=${isReadyStatus}&sortBy=${sortBy}`;
    if (range) {
        const toISO = (d: any) =>
            d instanceof Date ? d.toISOString() : new Date(d).toISOString();
        url += `&start=${toISO(range.startDate)}&end=${toISO(range.endDate)}`;
    }
    if (tourId && tourId !== 'all') {
        url += `&tourId=${tourId}`;
    }

    // Cache key based on the request URL (without the domain)
    const cacheKey = url;

    // Network-first strategy: always attempt the network, fall back to cache
    // on ANY failure (server unreachable, timeout, 5xx, etc.).
    // Note: navigator.onLine is intentionally NOT used here because it can
    // return true even when the backend is unreachable (e.g. no internet but
    // local network is up, or the API server itself is down).
    const res: any = await fetchClient.get(url, config()).catch(() => null);

    if (!res) {
        // Network failed — serve stale cache
        const cached = await offlineDb.getCachedOrdersList(cacheKey);
        return cached ? cached.data : null;
    }

    // Cache the fresh result for future offline use
    if (res.data) {
        await offlineDb.cacheOrdersList(cacheKey, res.data);
        // Also cache each order individually
        const orders = res.data?.orders ?? res.data?.data ?? [];
        if (Array.isArray(orders)) {
            await Promise.all(orders.map((o: any) => o?._id && offlineDb.cacheOrder(o)));
        }
    }

    return res.data;
};

export const getOrders = async () => {
    const res: any = await fetchClient
        .get(`/orders?limit=5000`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getOrder = async (id: string) => {
    // Network-first: try the server, fall back to local cache on any failure
    const res = await fetchClient.get(`/orders/${id}`, config()).catch(() => null);

    if (!res) {
        return await offlineDb.getCachedOrder(id);
    }

    if (res.data) {
        await offlineDb.cacheOrder(res.data);
    }
    return res.data;
};

export const SendDeliveryNote = async (id: string) => {
    const res = await fetchClient
        .get(`/orders/${id}/send-delivery-note`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const resolveOrderIssue = async (id: string) => {
    const res = await fetchClient
        .put(`/orders/${id}/resolve-issue`, {}, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const markOrderReady = async (id: string) => {
    const res = await fetchClient
        .put(`/orders/${id}/ready`, {}, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const addSavHistoryEntry = async (id: string, text: string) => {
    const res = await fetchClient
        .post(`/orders/${id}/sav/history`, { text }, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const resolveSavCase = async (id: string) => {
    const res = await fetchClient
        .put(`/orders/${id}/sav/resolve`, {}, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const duplicateOrder = async (id: string, itemIds?: string[]) => {
    const res: any = await fetchClient
        .post(`/orders/${id}/duplicate`, itemIds ? { itemIds } : {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const getOrderNote = async (
    id: string,
    isExternalPage: boolean = false,
) => {
    const fileConfig: any = {
        headers: {
            Authorization: config(isExternalPage).headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient
        .get(`/orders/${id}/order-note`, fileConfig)
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return new Blob([res.data], { type: 'application/pdf' });
};

export const getOrderDeliveryNote = async (
    id: string,
    isExternalPage: boolean = false,
) => {
    const fileConfig: any = {
        headers: {
            Authorization: config(isExternalPage).headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient
        .get(`/orders/${id}/delivery-note`, fileConfig)
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return new Blob([res.data], { type: 'application/pdf' });
};

export const getOrderDeliveryNoteWithoutPrice = async (
    id: string,
    isExternalPage: boolean = false,
) => {
    const fileConfig: any = {
        headers: {
            Authorization: config(isExternalPage).headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient
        .get(`/orders/${id}/delivery-note?hiddePrice=true`, fileConfig)
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return new Blob([res.data], { type: 'application/pdf' });
};

export const getOrdersByDate = async (date: string, deliveryTourId: string) => {
    const res: any = await fetchClient
        .get(`/orders/delivery/${date}/${deliveryTourId}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getOrdersDataByDate = async (
    date: string,
    deliveryTourId: string,
    tag: string,
    page: number,
    limit: number,
) => {
    const res: any = await fetchClient
        .get(
            `/orders/table?date=${date}&deliveryTourId=${deliveryTourId}&tag=${tag}&limit=${limit}&page=${page}`,
            config(),
        )
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const getOrdersQuantity = async () => {
    const res: any = await fetchClient
        .get(`/orders/quantity`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const updateOrder = async (customer: any) => {
    const id = customer._id;
    const body = { ...customer };
    delete body._id;

    // Attempt the network. Queue offline on any network error OR 5xx response
    // (covers local dev where the backend is reachable but MongoDB is not).
    let res: any = null;
    try {
        res = await fetchClient.put(`/orders/${id}`, body, config());
    } catch (err: any) {
        const status = err?.response?.status ?? 0;
        const isServerDown = status === 0 || status >= 500;
        if (isServerDown || !navigator.onLine) {
            // Queue mutation with token + conflict metadata
            await offlineDb.addPendingMutation({
                type: 'UPDATE_ORDER',
                payload: { ...customer },
                token: localStorage.getItem('token') || '',
                serverUpdatedAt: customer.updatedAt,
            });
            // Optimistic update in local cache
            const cached = await offlineDb.getCachedOrder(id);
            if (cached) await offlineDb.cacheOrder({ ...cached, ...customer, _id: id });
            return {
                data: null as any,
                error: {
                    code: 'OFFLINE_QUEUED',
                    message: 'La mise à jour est en attente de synchronisation.',
                } as any,
                offline: true,
            };
        }
        // 4xx — surface error to UI
        return { data: null as any, error: err?.response?.data ?? err?.message ?? 'Error' };
    }

    // Success — keep local cache in sync
    if (res?.data) {
        await offlineDb.cacheOrder({ ...customer, _id: id });
        return { data: res.data, error: null as any };
    }
    return { data: null as any, error: null as any };
};

export const bulkUpdateOrder = async (orders: any) => {
    const res: any = await fetchClient
        .put(`/orders/bulk`, orders, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const bulkEditOrderLotNumber = async (items: any) => {
    const res: any = await fetchClient
        .put(`/orders/add-lot-number`, { items }, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const archiveOrder = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/orders/${id}/archive`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const deleteOrder = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/orders/${id}`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const createOrder = async (customer: any) => {
    // Try the network regardless of navigator.onLine — the flag is unreliable
    // in local dev (WiFi cut = MongoDB unreachable on the backend, but
    // localhost:8080 is still technically reachable).
    let res: any = null;
    try {
        res = await fetchClient.post('/orders', customer, config());
    } catch (err: any) {
        // Queue offline if:
        //  - fetch threw a network error (ECONNREFUSED, ENOTFOUND, timeout)
        //  - OR the server returned 5xx (DB down, backend crash, etc.)
        const status = err?.response?.status ?? 0;
        const isServerDown = status === 0 || status >= 500;
        if (isServerDown || !navigator.onLine) {
            await offlineDb.addPendingMutation({
                type: 'CREATE_ORDER',
                payload: customer,
                token: localStorage.getItem('token') || '',
            });
            return {
                data: null as any,
                error: {
                    code: 'OFFLINE_QUEUED',
                    message: 'La création est en attente de synchronisation.',
                } as any,
                offline: true,
            };
        }
        // 4xx (validation error, auth, etc.) — surface the error to the UI
        return { error: err?.response?.data ?? err?.message ?? 'Error' };
    }

    if (!res?.data) return { error: 'Empty response' };
    return res.data;
};

export const computeOrderTotals = async (
    items: any[],
    customerId?: string,
): Promise<{ totalHT: number; totalTTC: number; isCustomerHT: boolean } | null> => {
    const res: any = await fetchClient
        .post('/orders/compute-totals', { items, customerId }, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

export interface ExtractedOrderItem {
    rawLabel: string;
    reference: string | null;
    quantity: number | null;
    unit: string | null;
    suggestions: {
        productId: string;
        name: string;
        ref: string;
        unity: string;
        price: number;
        vat: number;
        brutWeight: number;
        netWeight: number;
        score: number;
    }[];
}

export interface ExtractedOrderDocument {
    customerReference: string | null;
    deliveryDate: string | null;
    items: ExtractedOrderItem[];
}

// Sends a customer PDF/photo order to the AI extraction endpoint. Nothing is
// persisted server-side: the result is only a proposal for human review.
export const extractOrderDocument = async (
    file: File,
    customerId?: string,
): Promise<{ data?: ExtractedOrderDocument; error?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (customerId) formData.append('customerId', customerId);

    const token = localStorage.getItem('token');
    const response = await fetch(
        `${(await import('variables/config')).default.apiUrl}/orders/extract-document`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        },
    );

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "L'analyse du document a échoué." }));
        return { error: err.error || "L'analyse du document a échoué." };
    }
    const data = await response.json();
    return { data };
};

// Matches already-structured items (e.g. parsed client-side from an imported XLSX/CSV
// order file) against the product catalog. No AI involved: reuses the same server-side
// scoring logic as extractOrderDocument's matching step.
export const matchOrderItems = async (
    items: {
        rawLabel: string;
        reference: string | null;
        ean?: string | null;
        quantity: number;
        unit: string | null;
    }[],
    customerId?: string,
): Promise<ExtractedOrderItem[] | null> => {
    const res: any = await fetchClient
        .post('/orders/match-items', { items, customerId }, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data.items;
};
